import { useEffect, useState } from "react";

/** Read a media query once, SSR/test-safe (returns false when there is no
 * `window.matchMedia`). Kept pure so it is unit-testable without a DOM. */
export function mediaMatches(query: string): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query).matches
    : false;
}

/** Subscribe to a media query and re-render when it flips. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => mediaMatches(query));
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync immediately in case it changed before this effect ran
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** We drop the three-pane desktop layout for the Obsidian-style single-pane
 * shell (slide-in drawers + bottom dock) on:
 *   - anything narrower than `lg` (1024px) — phones + portrait tablets, and
 *   - touch-primary devices up to 1366px — i.e. tablets in LANDSCAPE (iPad
 *     landscape is 1180–1366px), the natural stylus-annotation posture.
 * This keeps a document full-width with the other panels collapsed into drawers
 * on every tablet, while mouse/trackpad laptops in that width range (pointer:
 * fine) keep the roomy three-pane desktop. */
export const COMPACT_QUERY =
  "(max-width: 1023px), (max-width: 1366px) and (pointer: coarse)";

export function useIsCompact(): boolean {
  return useMediaQuery(COMPACT_QUERY);
}

/** A TABLET is a compact device still wide enough to show two panes side by
 * side (≥ 768px) — i.e. an iPad, not a phone. On a tablet the file explorer
 * DOCKS to the left and pushes the document right (both usable at once) instead
 * of overlaying it as an inert modal drawer (the phone behaviour). */
export function useIsTablet(): boolean {
  // Compact (drawer shell) AND wide enough for a docked side panel.
  //
  // Both queries MUST be read unconditionally: `&&` short-circuits, so
  // `useMediaQuery("(min-width: 768px)")` used to run only while the first
  // query was already true. That meant the number of hooks this function
  // (and every caller of it, e.g. `App()`) invoked changed depending on
  // `compact` — a Rules-of-Hooks violation that corrupted the hook list on
  // every compact/desktop breakpoint crossing (React's "change in the order
  // of Hooks" warning, followed by a white-screen crash reading `.length` off
  // a hook slot that no longer held the value it used to).
  const compact = useMediaQuery(COMPACT_QUERY);
  const wideEnough = useMediaQuery("(min-width: 768px)");
  return compact && wideEnough;
}
